import axios, { AxiosResponse } from 'axios';
import { formatDistance } from 'date-fns';
import { ja } from 'date-fns/locale';
import { DateTime } from 'luxon';

import { Author, UserRole, ReactedUser, SourceSegments, CiCategories } from './API';
import { sourceSegmentsKeys, ciCategoriesKeys, ciCagetoriesLabel, Database, sourceSegmentsLabel } from './constants';

export const axiosRequest = async <T>(
  url: string,
  { arg }: { arg: { method?: string; body: T; token: string | null | undefined } }
) =>
  axios({
    method: arg.method ?? 'POST',
    headers: {
      'content-type': 'application/json',
      ...(arg.token && { Authorization: `Bearer ${arg.token}` }),
    },
    data: JSON.stringify(arg.body ?? {}),
    url,
  }).then((res: AxiosResponse<T>) => res.data);

export const getDateDistance = (isoString: string | undefined, lang: 'ja' | 'en') => {
  if (!isoString) {
    return '';
  }
  const dt = DateTime.fromISO(isoString, { zone: 'UTC' });
  const dateString = dt.setZone(DateTime.local().zoneName).toJSDate().toLocaleString('ja-JP', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  });
  return `${dateString}（${formatDistance(dt.setZone(DateTime.local().zoneName).toJSDate(), new Date(), {
    addSuffix: true,
    ...(lang === 'ja' && { locale: ja }),
  })}）`;
};

export const getDueDateDistance = (isoString: string | undefined, lang: 'ja' | 'en') => {
  const dueDt = DateTime.fromISO(isoString ?? '');
  const diff = dueDt.diff(DateTime.now().set({ hour: 0, minute: 0, second: 0, millisecond: 0 }), 'days');
  const diffDays = Math.round(diff.days);
  if (diffDays === 0) {
    return '本日中に回答してください';
  }
  if (diffDays < 0) {
    return '回答期限を過ぎています';
  }
  return `残り${diffDays}日`;
};

export const isExpired = (dueOn: string | undefined) => {
  const dt = DateTime.fromISO(dueOn ?? '');
  const diff = DateTime.now().diff(dt, 'days');
  const diffDays = Math.trunc(diff.days);
  if (diffDays > 0) {
    return true;
  }
  return false;
};

export const getDateString = (isoString: string | undefined) => {
  if (!isoString) {
    return '';
  }
  const dt = DateTime.fromISO(isoString, { zone: 'UTC' });
  return dt.setZone(DateTime.local().zoneName).toFormat('yyyy/MM/dd t');
};

export const isMarketing = (role: UserRole | undefined) => {
  if (role === UserRole.PMO || role === UserRole.PMO_APPROVER || role === UserRole.HQ) {
    return true;
  }
  return false;
};

export const isDisplayPersonName = (author: Author | ReactedUser | undefined) => {
  if (author?.firstName === 'Hidemasa' && author?.lastName === 'Tanigaki') {
    return true;
  }
  if (author?.firstName === 'Yuji' && author?.lastName === 'Liguori') {
    return true;
  }
  if (author?.firstName === 'Hiroaki' && author?.lastName === 'Atsuji') {
    return true;
  }
  if (author?.firstName === 'Nobuyasu' && author?.lastName === 'Takase') {
    return true;
  }
  return false;
};

export const isAdmin = (role: UserRole | undefined) => {
  if (role === UserRole.PMO || role === UserRole.PMO_APPROVER) {
    return true;
  }
  return false;
};

export const canCreatePost = (role: UserRole | undefined) => {
  if (role === UserRole.MR_APPROVER || role === UserRole.RM) {
    return false;
  }
  return true;
};

export const isApprovalRequired = (role: UserRole | undefined) => {
  if (role === UserRole.MR || role === UserRole.PMO) {
    return true;
  }
  return false;
};

// Utility function for browser interoperability
export const urlBase64ToUint8Array = (base64String: string) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

export const nonNullable = <T>(value: T): value is NonNullable<T> => value != null;

export const prepareBreadcrumbsKeys = (database: string, seg: SourceSegments, ca: CiCategories) => {
  let segments = sourceSegmentsKeys;
  let categories = ciCategoriesKeys;
  const categoriesLabel = { ...ciCagetoriesLabel };
  if (!ca.diseaseGroup) {
    // no category selected
    if (database === Database.RDBU) {
      categoriesLabel.diseaseGroup = 'フランチャイズ';
      categories = categories.filter((key) => key !== 'productGroup');
    }

    if (database === Database.JPBU1) {
      categoriesLabel.diseaseGroup = 'フランチャイズ';
      categories = categories.filter((key) => key !== 'activity');
    }
  } else {
    // remove unused keys if cagories are set
    segments = sourceSegmentsKeys.filter((key) => seg[key]?.id);
    categories = ciCategoriesKeys.filter((key) => ca[key]?.id);
  }

  return {
    segments,
    segmentsLabel: sourceSegmentsLabel,
    categories,
    categoriesLabel,
  };
};
